import { appConfigStore } from 'Models/AppConfigStore';
import * as uuid from 'uuid';
import { clearCopiedDriverDetails, ensureDriverAssigned, findDriverTicket } from 'Util/_HumanWritten/DriverAssignment';
import { isNullOrUndefined, stringIsEmpty } from 'Util/TypeGuards';
import { BookingSummary } from 'Views/Components/_HumanWritten/BookingSummaryDto';
import { IBookingFormState } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/context/BookingFormState';
import { BookingWizardData } from './BaseBookingWizardData';
import { PassengerBookingWizardData } from './PassengerBookingWizardData';
import { isVehicleBookingWizardData, VehicleBookingWizardData } from './VehicleBookingWizardData';

export class PassengerDetail {
	id: string;
	key: string;
	driver: boolean;

	firstName?: string;
	lastName?: string;
	phone?: string;
	email?: string;
	age?: number | null;
	dateOfBirth: Date | string | null | undefined;
	address?: string;
	gender?: string;

	/**
	 * True when this passenger row represents the primary booking contact (the
	 * "name on booking"). Used by the detail view to dedupe the primary name
	 * from the passenger-list breakdown.
	 */
	isPrimaryContact?: boolean;

	/**
	 * The fare class this passenger travels in. Undefined on a ferry with no classes, which is every
	 * client that has not configured them.
	 */
	passengerClassId?: string;

	/** Display name of that class, resolved server-side so no lookup is needed here. */
	passengerClassName?: string;

	/**
	 * The class's own colour, for the chip that names it at check-in.
	 *
	 * <p>The class's, not a vessel's override of it: an override is reached through the ferry, and
	 * the places that read this have an alteration rather than a vessel. Undefined where the class
	 * has no colour set, and the chip then takes the brand's.</p>
	 */
	passengerClassColour?: string;

	/**
	 * Whether this passenger's fare type lets a seat be picked, and so whether one with no seat is
	 * still waiting for one.
	 *
	 * <p>Undefined means not known — the query that built this did not load the fare type. Read as
	 * "say nothing": telling a passenger a seat is coming when their fare type has none is the worse
	 * of the two mistakes.</p>
	 */
	passengerClassAllowsSeatSelection?: boolean;

	/**
	 * The fare type on the <b>returning</b> leg.
	 *
	 * <p>One per leg, because that is how it is stored and priced: the fare type lives on the ticket,
	 * a ticket belongs to an alteration, and a return booking has one alteration per leg. A single
	 * value made the two legs share a choice — picking Premium for the way home silently upgraded the
	 * way out, and charged for it.</p>
	 */
	returningPassengerClassId?: string;

	/** Display name of the returning leg's fare type. */
	returningPassengerClassName?: string;

	/**
	 * The seat they hold on the <b>departing</b> sailing, when their class lets the booker choose one.
	 *
	 * <p>Unprefixed because it predates the return leg; renaming it would invalidate every wizard
	 * payload currently sitting in session storage mid-booking, for no gain.</p>
	 */
	seatId?: string;

	/** Display form of that seat ("A12"). Carried so check-in and the detail view need no lookup. */
	seatLabel?: string;

	/**
	 * The seat on the <b>returning</b> sailing — a different vessel on a different day, and so a
	 * different seat.
	 *
	 * <p>One field per leg. The server has always stored seats per leg (an assignment belongs to an
	 * alteration); this payload was the only thing insisting a passenger has one, which left every
	 * return booking seated outbound and unseated home.</p>
	 */
	returningSeatId?: string;

	/** Display form of the returning seat. */
	returningSeatLabel?: string;

	/**
	 * Whether the departing seat was handed out rather than chosen.
	 *
	 * <p>The seat-selection fee buys a choice. Everyone is seated automatically on arrival at the
	 * seating step, and charging for that would be charging for a seat nobody asked for — so the fee
	 * starts the moment the booker moves off the seat they were given.</p>
	 */
	seatAutoAssigned?: boolean;

	/** The same, for the returning leg. Each leg is charged on its own. */
	returningSeatAutoAssigned?: boolean;

	constructor(data?: Partial<PassengerDetail>) {
		Object.assign(this, data);
		// If the ID is empty, generate a new one
		if (stringIsEmpty(this.id)) {
			this.id = uuid.v4();
		}
		// If the key is empty, default to A
		if (stringIsEmpty(this.key)) {
			this.key = 'A';
		}
	}

	isEmpty = (): boolean => {
		return (
			stringIsEmpty(this.firstName) &&
			stringIsEmpty(this.lastName) &&
			stringIsEmpty(this.phone) &&
			stringIsEmpty(this.email) &&
			isNullOrUndefined(this.age) &&
			stringIsEmpty(this.address) &&
			stringIsEmpty(this.gender) &&
			isNullOrUndefined(this.dateOfBirth)
		);
	};

	// validate = (): PassengerError | null => {
	// 	const errors: { [key: string]: string } = {};
	// 	// Validation logic
	// 	if (appConfigStore.passengerFirstNameEnabled) {
	// 		if (stringIsEmpty(this.firstName)
	// 			&& appConfigStore.passengerFirstNameRequired) {
	// 			errors.firstName = 'Required field';
	// 		}
	//
	// 		if (checkIfInvalidName(this.firstName ?? '')) {
	// 			errors.firstName = 'No spaces. A-Z and \'-\' only.';
	// 		}
	// 	}
	//
	// 	if (appConfigStore.passengerLastNameEnabled) {
	// 		if (stringIsEmpty(this.lastName)
	// 			&& appConfigStore.passengerLastNameRequired) {
	// 			errors.lastName = 'Required field';
	// 		}
	//
	// 		if (checkIfInvalidName(this.lastName ?? '')) {
	// 			errors.lastName = 'No spaces. A-Z and \'-\' only.';
	// 		}
	// 	}
	//
	// 	if (appConfigStore.passengerPhoneEnabled) {
	// 		if (stringIsEmpty(this.phone)
	// 			&& appConfigStore.passengerPhoneRequired) {
	// 			errors.phone = 'Required field';
	// 		} else if (stringNotEmpty(this.phone)
	// 			&& !IsValidPhone(this.phone)) {
	// 			errors.phone = 'Enter a valid phone number';
	// 		}
	// 	}
	//
	// 	if (appConfigStore.passengerEmailEnabled) {
	// 		if (stringIsEmpty(this.email)
	// 			&& appConfigStore.passengerEmailRequired
	// 			&& appConfigStore.passengerEmailEnabled) {
	// 			errors.email = 'Required field';
	// 		}
	//
	// 		if (stringNotEmpty(this.email) && !isEmail(this.email)) {
	// 			errors.email = 'Enter a valid email address (xx@xx.xx)';
	// 		}
	// 	}
	//
	// 	if (isNullOrUndefined(this.age)
	// 		&& appConfigStore.passengerAgeRequired
	// 		&& appConfigStore.passengerAgeEnabled) {
	// 		errors.age = 'Required field';
	// 	}
	//
	// 	if (stringIsEmpty(this.address)
	// 		&& appConfigStore.passengerAddressRequired
	// 		&& appConfigStore.passengerAddressEnabled) {
	// 		errors.address = 'Required field';
	// 	}
	//
	// 	if (stringIsEmpty(this.gender)
	// 		&& appConfigStore.passengerGenderRequired
	// 		&& appConfigStore.passengerGenderEnabled) {
	// 		errors.gender = 'Required field';
	// 	}
	//
	// 	if (isNullOrUndefined(this.dateOfBirth)
	// 		&& appConfigStore.passengerDateOfBirthRequired
	// 		&& appConfigStore.passengerDateOfBirthEnabled) {
	// 		errors.dateOfBirth = 'Required field';
	// 	}
	//
	// 	return Object.keys(errors).length > 0
	// 		? { id: this.id, errors }
	// 		: null;
	// }
}

/**
 * A passenger's fare class on one leg, with its name.
 *
 * <p>The name travels with the id because the wizard shows it in places that hold no class list —
 * the passenger-removal modal is opened from the sidebar counter, long before the seating step
 * fetches one. Setting the id alone left the name blank on every new booking, or worse, still naming
 * the class the passenger was in before.</p>
 */
export function passengerClassPatch(
	leg: 'departure' | 'return',
	passengerClassId: string | undefined,
	passengerClassName: string | undefined,
): Partial<PassengerDetail> {
	return leg === 'return'
		? { returningPassengerClassId: passengerClassId, returningPassengerClassName: passengerClassName }
		: { passengerClassId, passengerClassName };
}

export function getDefaultPassengerInfo(key: string, isDriver: boolean): PassengerDetail {
	return new PassengerDetail({
		id: uuid.v4(),
		key: key,
		firstName: '',
		lastName: '',
		phone: '',
		email: '',
		age: null,
		dateOfBirth: null,
		address: '',
		gender: '',
		driver: isDriver ?? false,
	});
}

export function getWizardDataPassengerTicketDependencies(wizardData: BookingWizardData) {
	const innerData = wizardData.innerWizardData as PassengerBookingWizardData | VehicleBookingWizardData;
	return [
		innerData?.adultTickets?.length,
		innerData?.childTickets?.length,
		innerData?.infantTickets?.length,
		innerData?.passengerDTickets?.length,
		innerData?.passengerETickets?.length,
		innerData?.passengerFTickets?.length,
		innerData?.passengerGTickets?.length,
		innerData?.passengerHTickets?.length,
	];
}

/**
 * Copies the booking's driver name / phone onto whichever passenger holds the driver flag,
 * assigning one first if nobody does.
 *
 * <b>Searches every passenger type, not just adults.</b> This used to look only at `adultTickets`
 * and, finding no driver there, mark `adultTickets[0]` as the driver — so choosing a driver of any
 * other type produced a *second* driver the next time this ran, and handed the role back to an
 * adult. `PassengerTypeEntity.CanBeDriver` is configurable per type, which is exactly the
 * assumption that broke.
 */
export function assignDriverDetailsToPassenger(wizardData: BookingWizardData) {
	// Only a vehicle booking has a driver. The walk-on tab keeps the flags in memory now rather than
	// clearing them (they are stripped at submit), so without this a call on the passenger tab would
	// assign a driver to a booking that is not carrying a vehicle.
	if (!isVehicleBookingWizardData(wizardData.innerWizardData)) {
		return;
	}
	const innerData = wizardData.innerWizardData;

	// Ungated, and that is the point. These are two jobs, and they used to share one config check:
	// "make sure somebody is driving" and "copy the driver's name onto them". Only the second has
	// anything to do with whether the operator collects passenger names — the first decides which
	// passenger type the measurement filter reads, which is a pricing input on every tenant. With
	// both behind the gate, a names-off operator could pick a driver in the sidebar and have nothing
	// happen at all.
	ensureDriverAssigned(innerData);

	if (!appConfigStore.passengerInfoEnabled) {
		return;
	}

	// Whoever drove before keeps the driver's name otherwise, and the booking then lists two
	// passengers as the same person.
	clearCopiedDriverDetails(innerData, {
		firstName: innerData.driverFirstName,
		lastName: innerData.driverLastName,
		phone: innerData.driverPhone,
	});

	const driverCustomer = findDriverTicket(innerData);

	if (driverCustomer) {
		driverCustomer.firstName = innerData.driverFirstName;
		driverCustomer.lastName = innerData.driverLastName;
		driverCustomer.phone = innerData.driverPhone;
	}
}

/** The check-in counterpart of {@link assignDriverDetailsToPassenger}, with the same split. */
export function assignDriverDetailsToPassengerInCheckIn(formData: IBookingFormState) {
	if (formData.type !== 'vehicle') {
		return;
	}

	ensureDriverAssigned(formData);

	if (!appConfigStore.passengerInfoEnabled) {
		return;
	}

	clearCopiedDriverDetails(formData, {
		firstName: formData.driverFirstName,
		lastName: formData.driverLastName,
		phone: formData.driverPhone,
	});

	const driverCustomer = findDriverTicket(formData);

	if (driverCustomer) {
		driverCustomer.firstName = formData.driverFirstName;
		driverCustomer.lastName = formData.driverLastName;
		driverCustomer.phone = formData.driverPhone;
	}
}
